/**
 * Image loading for the players PDF export.
 *
 * Every image that reaches jsPDF is a validated data URL (or a bundled
 * fallback), so a slow CDN, a missing CORS header or a broken file can never
 * leave a blank box or stop the PDF from being generated.
 */

const MIN_DATA_URL_LENGTH = 200;
const DEFAULT_TIMEOUT_MS = 8000;

export const getJsPdfImageFormat = (src) => {
  if (!src || typeof src !== "string") return "JPEG";
  if (src.startsWith("data:image/png")) return "PNG";
  if (src.startsWith("data:image/webp")) return "WEBP";
  if (src.startsWith("data:image/jpeg") || src.startsWith("data:image/jpg"))
    return "JPEG";
  if (/\.png(\?|$)/i.test(src)) return "PNG";
  return "JPEG";
};

export const resolvePlayerPdfImage = (imagesMap, pid, fallback) => {
  if (!imagesMap) return fallback;
  const key = String(pid);
  return imagesMap[key] || imagesMap[pid] || fallback;
};

const isUsableDataUrl = (value) =>
  typeof value === "string" &&
  value.startsWith("data:image") &&
  value.length > MIN_DATA_URL_LENGTH;

export const withTimeout = (promise, ms, label = "timeout") =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(label)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });

/** Unique query token so the browser never reuses a cached non-CORS copy (that taints the canvas). */
const withCorsCacheBust = (url) => {
  if (!url || url.startsWith("data:") || url.startsWith("blob:")) return url;
  const token = `pdfimg=${Date.now()}_${Math.floor(Math.random() * 1e6)}`;
  return url.includes("?") ? `${url}&${token}` : `${url}?${token}`;
};

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("FileReader failed"));
    reader.readAsDataURL(blob);
  });

const loadViaImageElement = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.decoding = "async";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("image element failed"));
    img.src = withCorsCacheBust(url);
  });

const loadViaFetch = async (url) => {
  const response = await fetch(withCorsCacheBust(url), {
    mode: "cors",
    credentials: "omit",
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const blob = await response.blob();
  if (!blob.size) throw new Error("empty image");
  return createImageBitmap(blob);
};

const sourceSize = (src) => ({
  w: src.naturalWidth || src.width || 0,
  h: src.naturalHeight || src.height || 0,
});

const fitInside = (w, h, maxW, maxH) => {
  const scale = Math.min(1, maxW / w, maxH / h);
  return {
    w: Math.max(1, Math.round(w * scale)),
    h: Math.max(1, Math.round(h * scale)),
  };
};

/** Square photo on white (JPEG has no alpha, so transparent PNG areas must not turn black). */
const toPhotoJpeg = (src, maxSide = 400, quality = 0.8) => {
  const { w, h } = sourceSize(src);
  if (!w || !h) throw new Error("zero size image");
  const size = fitInside(w, h, maxSide, maxSide);
  const canvas = document.createElement("canvas");
  canvas.width = size.w;
  canvas.height = size.h;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, size.w, size.h);
  ctx.drawImage(src, 0, 0, size.w, size.h);
  return canvas.toDataURL("image/jpeg", quality);
};

/** Circle-cropped photo; PNG keeps the corners outside the circle transparent. */
const toRoundPng = (src, maxSide = 300) => {
  const { w, h } = sourceSize(src);
  if (!w || !h) throw new Error("zero size image");
  const crop = Math.min(w, h);
  const size = Math.min(maxSide, crop);
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2, true);
  ctx.closePath();
  ctx.clip();
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, size, size);
  ctx.drawImage(src, (w - crop) / 2, (h - crop) / 2, crop, crop, 0, 0, size, size);
  return canvas.toDataURL("image/png");
};

/** Logos keep transparency. */
const toLogoPng = (src, maxSide = 600) => {
  const { w, h } = sourceSize(src);
  if (!w || !h) throw new Error("zero size image");
  const size = fitInside(w, h, maxSide, maxSide);
  const canvas = document.createElement("canvas");
  canvas.width = size.w;
  canvas.height = size.h;
  canvas.getContext("2d").drawImage(src, 0, 0, size.w, size.h);
  return canvas.toDataURL("image/png");
};

/** Full-page custom background (A4 at ~150dpi is plenty). */
const toBackgroundJpeg = (src) => {
  const { w, h } = sourceSize(src);
  if (!w || !h) throw new Error("zero size image");
  const size = fitInside(w, h, 1240, 1754);
  const canvas = document.createElement("canvas");
  canvas.width = size.w;
  canvas.height = size.h;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, size.w, size.h);
  ctx.drawImage(src, 0, 0, size.w, size.h);
  return canvas.toDataURL("image/jpeg", 0.85);
};

/**
 * Loads a remote image and converts it with `convert`. The <img> and fetch()
 * paths race; whichever produces a usable (non-tainted) result first wins.
 * Resolves to null on failure – never throws, never hangs past the timeout.
 */
const loadRemoteConverted = (
  url,
  convert,
  isValid = isUsableDataUrl,
  timeoutMs = DEFAULT_TIMEOUT_MS
) => {
  if (!url) return Promise.resolve(null);
  const attempt = (loader) =>
    withTimeout(
      loader(url).then((src) => {
        try {
          const out = convert(src);
          if (!isValid(out)) throw new Error("empty conversion");
          return out;
        } finally {
          if (src && typeof src.close === "function") src.close();
        }
      }),
      timeoutMs,
      "image timeout"
    );

  return new Promise((resolve) => {
    let pending = 2;
    let settled = false;
    const onFail = () => {
      pending -= 1;
      if (!settled && pending <= 0) {
        settled = true;
        resolve(null);
      }
    };
    const onOk = (value) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    attempt(loadViaImageElement).then(onOk, onFail);
    attempt(loadViaFetch).then(onOk, onFail);
  });
};

/** Bundled (same-origin) asset → exact-bytes data URL. Falls back to the URL itself. */
export const loadStaticAsset = async (asset) => {
  if (!asset || typeof asset !== "string") return asset;
  if (asset.startsWith("data:")) return asset;
  try {
    const response = await withTimeout(fetch(asset), DEFAULT_TIMEOUT_MS);
    if (!response.ok) return asset;
    const dataUrl = await blobToDataUrl(await response.blob());
    return isUsableDataUrl(dataUrl) ? dataUrl : asset;
  } catch (e) {
    return asset;
  }
};

export const loadStaticAssets = async (assetMap) => {
  const entries = await Promise.all(
    Object.entries(assetMap).map(async ([key, value]) => [
      key,
      await loadStaticAsset(value),
    ])
  );
  return Object.fromEntries(entries);
};

/** Runs `worker` over `items` with limited concurrency, reporting progress (0–1). */
export const runWithConcurrency = async (items, worker, concurrency, onProgress) => {
  let done = 0;
  let index = 0;
  const total = items.length;
  const next = async () => {
    while (index < total) {
      const item = items[index];
      index += 1;
      await worker(item);
      done += 1;
      if (onProgress) onProgress(total ? done / total : 1);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(concurrency, total) }, () => next())
  );
  if (onProgress) onProgress(1);
};

/**
 * Creates a loader with a per-session cache, so a second download does not
 * re-fetch every photo.
 */
export const createPdfImageLoader = () => {
  const cache = new Map();
  const cached = (key, factory) => {
    if (!cache.has(key)) cache.set(key, factory());
    return cache.get(key);
  };

  const loadPlayerPhoto = (url) =>
    cached(`player:${url}`, async () => {
      const photo = await loadRemoteConverted(
        url,
        (src) => {
          const regular = toPhotoJpeg(src);
          let round = null;
          try {
            round = toRoundPng(src);
          } catch (e) {
            round = null;
          }
          return { regular, round: isUsableDataUrl(round) ? round : regular };
        },
        (out) => Boolean(out) && isUsableDataUrl(out.regular)
      );
      if (!photo) cache.delete(`player:${url}`);
      return photo;
    });

  const loadLogo = (url) =>
    cached(`logo:${url}`, async () => {
      const out = await loadRemoteConverted(url, (src) => toLogoPng(src));
      if (!out) cache.delete(`logo:${url}`);
      return out;
    });

  const loadBackground = (url) =>
    cached(`bg:${url}`, async () => {
      const out = await loadRemoteConverted(url, (src) => toBackgroundJpeg(src));
      if (!out) cache.delete(`bg:${url}`);
      return out;
    });

  return { loadPlayerPhoto, loadLogo, loadBackground };
};

/**
 * Wraps doc.addImage so that:
 * - the format always matches the real data (JPEG vs PNG mismatches render blank),
 * - empty images are skipped instead of throwing,
 * - one bad image never aborts the whole PDF.
 */
export const makeSafeAddImage = (doc) => {
  const original = doc.addImage.bind(doc);
  return function safeAddImage(imageData, format, ...rest) {
    if (imageData == null || imageData === "") return doc;
    const fmt =
      typeof imageData === "string" && imageData.startsWith("data:image")
        ? getJsPdfImageFormat(imageData)
        : format;
    try {
      return original(imageData, fmt, ...rest);
    } catch (error) {
      console.warn("PDF: image skipped", error);
      return doc;
    }
  };
};
